import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import * as utils from './utils';
import { Entity } from 'src/app/core/models/entity.model';
import { Team } from 'src/app/core/models/team.model';
import { HorizonsTeam } from 'src/app/core/models/horizons-team.model';
import { PackageType } from 'src/app/core/models/package-type.model';
import { Config } from 'src/app/core/models/config.model';
import { Validators } from '@angular/forms';

export class CustomValidators {

  static emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  static alphaRegex = /^[a-zA-Z\u00C0-\u00FF\s]*$/;

  static getValidatorErrorMessage(validatorName: string, validatorValue?: unknown) {
    const config = {
      required: { message: 'REQUIRED', param: '' },
      email: { message: 'INVALID_EMAIL', param: '' },
      emailNotLowercase: { message: 'EMAIL_NOT_LOWERCASE', param: '' },
      invalidUser: { message: 'INVALID_USER', param: '' },
      invalidPassword: { message: 'INVALID_PASSWORD', param: '' },
      invalidPasswordFormat: { message: 'INVALID_PASSWORD_FORMAT', param: '' },
      notSamePassword: { message: 'NOT_SAME_PASSWORD', param: '' },
      minlength: { message: 'MINIMUNLENGTH', param: utils.getProperty(validatorValue, 'requiredLength') },
      requiredWith: { message: 'REQUIRED', param: utils.getProperty(validatorValue, 'field') },
      invalidDocument: { message: 'INVALIDDOCUMENT', param: '' },
      noAlphanumeric: { message: 'NOALPHANUMERIC', param: '' },
      notValidCharacters: { message: 'NOTVALIDCHARACTERS', param: '' },
      pattern: { message: 'INVALID', param: '' },
      max: { message: 'MAX', param: utils.getProperty(validatorValue, 'max') },
      maxPackages: { message: 'MAXPACKAGES', param: utils.getProperty(validatorValue, 'max') },
      min: { message: 'MIN', param: utils.getProperty(validatorValue, 'min') },
      shopDemo: { message: 'INVALID_SHOP_CATEGORY', param: '' },
      privatePackage: { message: 'PRIVATE_PACKAGE', param: '' },
      invalidUsernameFormat: { message: 'INVALID_USERNAME_FORMAT', param: '' },
      teamNameUsed: { message: 'TEAMNAMEUSED', param: utils.getProperty(validatorValue, 'TEAMNAMEUSED') },
      horizonsTeamNameUsed: { message: 'HORIZONSTEAMNAMEUSED', param: utils.getProperty(validatorValue, 'HORIZONSTEAMNAMEUSED') }
    };

    return config[validatorName as keyof typeof config];
  }

  static passwordValidator(control: FormControl) {
    // {6,15}                      - Assert password is between 6 and 15 characters
    // (?=.*\d)                    - Assert a string has at least one number
    // (?=.*[a-z])(?=.*[A-Z])      - Assert a string has at least one upper case and one lower case
    if (control.value && !control.value.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d_.\-!@$?¿*()\[\]+=|,%&~¬'{}ºª\\/\"·#]{8,15}$/)) {
      return { invalidPasswordFormat: true };
    } else {
      return null;
    }
  }

  static usernameValidator(control: AbstractControl) {
    // Assert username is between 4 and 30 characters
    // Assert username is alphanumeric _ . - characters
    if (control.value && !control.value.match(/^(?!^0+$)[a-zA-Z0-9_.-]{4,30}$/)) {
      return { invalidUsernameFormat: true };
    } else {
      return null;
    }
  }

  static lowercaseEmailValidator(control: AbstractControl) {
    // First check if email format is valid
    const emailValidator = Validators.email(control);
    if (emailValidator !== null) {
      return emailValidator;
    }

    // Then check if email is in lowercase
    if (control.value && control.value !== control.value.toLowerCase()) {
      return { emailNotLowercase: true };
    }

    return null;
  }
  static teamnameValidator(_entity: FormControl, _teams: Team[], _teamName: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {
      if (_teamName && _teamName.toLowerCase().trim() === control.value?.toLowerCase().trim()) return null
      let partialTeams: Team[] = _teams?.filter((t: Team) => t.entityId === _entity.value)
      if (partialTeams?.map((t: Team) => t.name.toLowerCase().trim()).includes(control.value?.toLowerCase().trim())) {
        return { teamNameUsed: true };
      } else {
        return null;
      }
    };
  }

  static horizonsTeamnameValidator(_horizonsTeams: HorizonsTeam[], _horizonsTeamName: string): ValidatorFn {

    return (control: AbstractControl): ValidationErrors | null => {
      console.log(control.value);
      console.log(_horizonsTeams);
      console.log(_horizonsTeamName);
      if (_horizonsTeamName && _horizonsTeamName.toLowerCase().trim() === control.value?.toLowerCase().trim()) return null
      if (_horizonsTeams?.map((ht: HorizonsTeam) => ht.name.toLowerCase().trim()).includes(control.value?.toLowerCase().trim())) {
        return { horizonsTeamNameUsed: true };
      } else {
        return null;
      }
    };
  }

  static documentValidator(control: FormControl) {
    if (control.value && !control.value.match(/(([X-Zx-z]{1})([-]?)(\d{7})([-]?)([A-Za-z]{1}))|((\d{8})([-]?)([A-Za-z]{1}))/)) {
      return { invalidDocument: true };
    } else {
      return null;
    }
  }

  static passportValidator(control: FormControl) {
    if (control.value && !control.value.match(/^(?!^0+$)[a-zA-Z0-9]{6,9}$/)) {
      return { invalidDocument: true };
    } else {
      return null;
    }
  }

  static requiredWith(field: string) {
    return (control: FormControl) => {
      if (control.parent?.get(field)?.value && !control.value) {
        return { requiredWith: true };
      } else {
        return null;
      }
    };
  }

  static alphanumeric(control: FormControl) {
    if (control.value && !control.value.match(/^[a-zA-Z0-9]*$/)) {
      return { noAlphanumeric: true };
    } else {
      return null;
    }
  }

  static validCharacters(control: FormControl) {
    if (control.value && control.value.match(/(¡|!|\(|\)|~|\?|\^|\[|\]|\+|\*|-|_|\{|\}|;|,|\/)/)) {
      return { notValidCharacters: true };
    } else {
      return null;
    }
  }

  static maxIntervalOneyear(group: FormGroup): any {
    /*if ((typeof group.controls['fechaDesde'].value) === 'string' && (typeof group.controls['fechaHasta'].value) === 'string' ) {
        let dateString = group.controls['fechaDesde'].value;
        let dateParts = dateString.split("/");
        let dateObject = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
        let fromMilli = dateObject.getTime();
        dateString = String(group.controls['fechaHasta'].value); // Oct 23
        dateParts = dateString.split("/");
        dateObject = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
        let toMilli = dateObject.getTime();
        let diff = toMilli - fromMilli;
        //console.log(diff);
        if (diff < 0) {
            return { 'reverse': true }
        } else if (diff > 31536000000) {
            return { 'exceed': true }
        } else {
            return null
        }
    }else{
        return null
    }*/
  }

  static ShopCategoryPackage(entityId: FormControl, categoryId: FormControl, entities: Entity[]): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let match: Entity = entities.find((entity: Entity) => entity.id === entityId.value)!;
      if (Number(match?.entityTypeId) !== 4 && Number(categoryId.value) === 2) {
        return { shopDemo: true }
      } else {
        return null
      }
    };
  }

  static ShopNeeded(form: FormGroup, entities: Entity[], config: Config, formControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let match: Entity = entities.find((entity: Entity) => entity.id === Number(form.get('entityId')!.value))!;
      let error;
      let errorPrivacity;
      if (Number(match?.entityTypeId) !== 4 && config.packageTypes?.find((packageType: PackageType) => packageType.id === form.get('packageTypeId')?.value)?.private!) {
        errorPrivacity = { privatePackage: true }
      } else {
        errorPrivacity = null;
      }
      if (Number(match?.entityTypeId) !== 4 && Number(form.get('packageCategoryId')!.value) === 2) {
        error = { shopDemo: true }
      } else {
        error = null
      }
      switch (formControlName) {
        case 'packageTypeId':
          return errorPrivacity
        case 'packageCategoryId':
          return error
        case 'entityId':
          form.get('packageCategoryId')?.setErrors(error)
          form.get('packageTypeId')?.setErrors(errorPrivacity)
          break;
        default:
          break;
      }
      return null
    };
  }
}
